import { useEffect, useMemo, useRef } from "react";
import {
  CurrencyNetwork,
  DEPOSIT_ADDRESS_STATE,
  LimitsReponse,
  OPERATIONS_VALUES,
  REMAINING_LIMITS_CATEGORY,
  TIME_PERIOD_VALUES,
} from "@letsbit/malcolmlb";
import { FormattedMessage } from "react-intl";
import { animated, useTransition } from "@react-spring/web";

import MessagesComponent from "components/MessagesWithLinks";
import { messages } from "./messages";
import { DepositPersonalAccountCryptoProps } from "./types";
import CryptoQrCard from "../CryptoQrCard";
import { useCurrency } from "hooks/user/useCurrencies";
import { Pill } from "components/Pill";
import { useMutateAll } from "hooks/utils/useMutateAll";
import { SWR_WALLET_ADDRESS_KEY } from "hooks/payments/useGetDepositAddress";
import { useGetLimits } from "hooks/payments/useGetLimits";

export const DepositPersonalAccountCrypto: React.FC<
  DepositPersonalAccountCryptoProps
> = ({
  currencyId,
  paymentNetworkId,
  depositAddress,
  setPaymentNetwork,
  viewerOnly = false,
}) => {
  const currency = useCurrency(currencyId);
  const { mutateAll } = useMutateAll();
  const timeout = useRef<NodeJS.Timeout>();

  const { data: operationalLimits } = viewerOnly
    ? { data: undefined }
    : useGetLimits({
        currency: currencyId,
        payment_network: paymentNetworkId || "",
        operation: OPERATIONS_VALUES.DEPOSIT,
        category: REMAINING_LIMITS_CATEGORY.OPERATIONAL,
      });

  const operationalMonthlyLimit = useMemo(() => {
    return operationalLimits?.find(
      (l: LimitsReponse) =>
        l.time_period === TIME_PERIOD_VALUES.MONTHLY && l.kind === "volume",
    );
  }, [operationalLimits]);

  const paymentNetworksSelector = useMemo(
    () =>
      currency?.currency_networks
        .filter((cn) => cn.deposit_enabled)
        .map((n) => {
          return { label: n.payment_network.name, value: n };
        }),
    [currency?.currency_networks],
  );

  const currencyNetworkSelected: CurrencyNetwork | undefined = useMemo(
    () =>
      currency?.currency_networks.filter(
        (cn) =>
          cn.deposit_enabled && cn.payment_network.id === paymentNetworkId,
      )[0],
    [currency?.currency_networks, paymentNetworkId],
  );

  const actionsTransitions = useTransition(currencyNetworkSelected, {
    key: currencyNetworkSelected,
    from: { opacity: 0, x: 20 },
    enter: { opacity: 1, x: 0 },
    leave: { opacity: 0, x: -20 },
    config: { duration: 350 },
    exitBeforeEnter: true,
  });

  useEffect(() => {
    if (depositAddress.data.state === DEPOSIT_ADDRESS_STATE.PENDING) {
      timeout.current = setTimeout(
        () => mutateAll(SWR_WALLET_ADDRESS_KEY),
        2000,
      );
    }
    return () => timeout.current && clearTimeout(timeout.current);
  }, [mutateAll, depositAddress.data.state]);

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <div className="flex flex-col">
        <h3 className="mb-2 text-lg text-[--color-neutral-0]">
          <FormattedMessage
            {...messages.selectNetwork}
            values={{ ticker: currency?.ticker_id.toUpperCase() }}
          />
        </h3>
        <div className="flex flex-row items-center gap-x-2">
          {paymentNetworksSelector?.map((paymentNetwork) => (
            <Pill
              onClick={() => {
                setPaymentNetwork(paymentNetwork.value);
              }}
              selected={
                currencyNetworkSelected?.payment_network.id ===
                paymentNetwork.value.payment_network.id
              }
            >
              {paymentNetwork.label}
            </Pill>
          ))}
        </div>
      </div>
      {actionsTransitions(
        (style, i) =>
          i && (
            <animated.div style={style} className="flex flex-col gap-2">
              <CryptoQrCard
                depositAddress={depositAddress}
                currency={currency}
                currencyNetworkSelected={currencyNetworkSelected}
                monthlyLimit={operationalMonthlyLimit}
              />
            </animated.div>
          ),
      )}
      <MessagesComponent messages={currencyNetworkSelected?.deposit_messages} />
    </div>
  );
};
